/**
 * Circuit Breaker Pattern Implementation
 * Prevents cascading failures by stopping requests to failing services
 *
 * States:
 * - CLOSED: Normal operation, requests go through
 * - OPEN: Failing, requests rejected immediately
 * - HALF_OPEN: Testing recovery, one request allowed to test service
 */

/**
 * Circuit breaker state
 */
export type CircuitBreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

/**
 * Configuration for circuit breaker behavior
 */
export interface CircuitBreakerConfig {
  /** Number of consecutive failures before opening circuit */
  failureThreshold: number;

  /** Number of successful requests in HALF_OPEN state to close circuit */
  successThreshold: number;

  /** Milliseconds to wait before transitioning from OPEN to HALF_OPEN */
  timeout: number;

  /** Maximum requests allowed in HALF_OPEN state */
  halfOpenMaxRequests: number;
}

/**
 * Default circuit breaker configuration
 * - Opens after 5 consecutive failures
 * - Closes after 2 successes in HALF_OPEN state
 * - Waits 30 seconds before attempting recovery
 */
export const DEFAULT_CIRCUIT_BREAKER_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 5,
  successThreshold: 2,
  timeout: 30000,
  halfOpenMaxRequests: 3,
};

/**
 * Aggressive circuit breaker (opens quickly)
 * - Opens after 2 failures
 * - Closes after 1 success
 * - Waits 10 seconds
 */
export const AGGRESSIVE_CIRCUIT_BREAKER_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 2,
  successThreshold: 1,
  timeout: 10000,
  halfOpenMaxRequests: 1,
};

/**
 * Tolerant circuit breaker (opens slowly)
 * - Opens after 10 failures
 * - Closes after 5 successes
 * - Waits 60 seconds
 */
export const TOLERANT_CIRCUIT_BREAKER_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 10,
  successThreshold: 5,
  timeout: 60000,
  halfOpenMaxRequests: 5,
};

/**
 * Circuit breaker error thrown when circuit is OPEN
 */
export class CircuitBreakerOpenError extends Error {
  readonly state: CircuitBreakerState = 'OPEN';
  readonly nextAttempt: Date;

  constructor(nextAttempt: Date) {
    super(`Circuit breaker is OPEN. Next attempt at ${nextAttempt.toISOString()}`);
    this.name = 'CircuitBreakerOpenError';
    this.nextAttempt = nextAttempt;
  }
}

/**
 * State tracking for circuit breaker
 */
interface CircuitBreakerMetrics {
  state: CircuitBreakerState;
  failureCount: number;
  successCount: number;
  lastFailureTime?: number;
  lastSuccessTime?: number;
  lastStateChangeTime: number;
  halfOpenRequests: number;
}

/**
 * Callback hooks for circuit breaker events
 */
export interface CircuitBreakerHooks {
  /** Called when circuit transitions to OPEN */
  onOpen?: (failureCount: number, lastError?: Error) => void;

  /** Called when circuit transitions to CLOSED */
  onClosed?: (successCount: number) => void;

  /** Called when circuit transitions to HALF_OPEN */
  onHalfOpen?: () => void;

  /** Called before request in HALF_OPEN state */
  onHalfOpenRequest?: (requestNumber: number) => void;

  /** Called when request rejected due to open circuit */
  onRejected?: (error: CircuitBreakerOpenError) => void;
}

/**
 * Circuit Breaker implementation
 *
 * Prevents cascading failures by:
 * 1. Tracking request failures
 * 2. Opening circuit when threshold exceeded
 * 3. Rejecting requests when open
 * 4. Testing recovery in HALF_OPEN state
 * 5. Closing circuit on recovery
 *
 * @example
 * ```typescript
 * const breaker = new CircuitBreaker({
 *   failureThreshold: 5,
 *   successThreshold: 2,
 *   timeout: 30000,
 * });
 *
 * try {
 *   await breaker.execute(() => apiClient.get('/users'));
 * } catch (error) {
 *   if (error instanceof CircuitBreakerOpenError) {
 *     console.log('Service unavailable, retry after:', error.nextAttempt);
 *   }
 * }
 * ```
 */
export class CircuitBreaker {
  private config: CircuitBreakerConfig;
  private metrics: CircuitBreakerMetrics;
  private hooks: CircuitBreakerHooks;
  private lastError?: Error;

  constructor(config: Partial<CircuitBreakerConfig> = {}, hooks: CircuitBreakerHooks = {}) {
    this.config = {
      ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
      ...config,
    };
    this.validateConfig();

    this.metrics = {
      state: 'CLOSED',
      failureCount: 0,
      successCount: 0,
      halfOpenRequests: 0,
      lastStateChangeTime: Date.now(),
    };

    this.hooks = hooks;
  }

  /**
   * Get current circuit breaker state
   */
  getState(): CircuitBreakerState {
    return this.metrics.state;
  }

  /**
   * Get detailed metrics
   */
  getMetrics() {
    return {
      state: this.metrics.state,
      failureCount: this.metrics.failureCount,
      successCount: this.metrics.successCount,
      lastFailureTime: this.metrics.lastFailureTime,
      lastSuccessTime: this.metrics.lastSuccessTime,
      lastError: this.lastError?.message,
      halfOpenRequests: this.metrics.halfOpenRequests,
    };
  }

  /**
   * Get time until next attempt (when in OPEN state)
   */
  getNextAttemptTime(): Date | null {
    if (this.metrics.state !== 'OPEN') {
      return null;
    }

    const nextAttempt = new Date(
      this.metrics.lastStateChangeTime + this.config.timeout
    );
    return nextAttempt;
  }

  /**
   * Reset circuit breaker to initial state
   * Useful for testing or manual recovery
   */
  reset(): void {
    this.metrics = {
      state: 'CLOSED',
      failureCount: 0,
      successCount: 0,
      halfOpenRequests: 0,
      lastStateChangeTime: Date.now(),
    };
    this.lastError = undefined;
  }

  /**
   * Manually open the circuit (for operational emergencies)
   */
  open(): void {
    this.transitionTo('OPEN');
  }

  /**
   * Manually close the circuit (for manual recovery)
   */
  close(): void {
    this.transitionTo('CLOSED');
    this.metrics.failureCount = 0;
    this.metrics.successCount = 0;
    if (this.hooks.onClosed) {
      this.hooks.onClosed(0);
    }
  }

  /**
   * Execute a request with circuit breaker protection
   *
   * @param fn Function that returns a promise
   * @returns Promise that resolves if request succeeds
   * @throws CircuitBreakerOpenError if circuit is OPEN
   * @throws Error from fn if it fails
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Check if we need to transition from OPEN to HALF_OPEN
    if (this.metrics.state === 'OPEN') {
      const timeSinceOpen = Date.now() - this.metrics.lastStateChangeTime;
      if (timeSinceOpen >= this.config.timeout) {
        this.transitionTo('HALF_OPEN');
      } else {
        // Circuit is still open, reject immediately
        const nextAttempt = new Date(
          this.metrics.lastStateChangeTime + this.config.timeout
        );
        const error = new CircuitBreakerOpenError(nextAttempt);
        if (this.hooks.onRejected) {
          this.hooks.onRejected(error);
        }
        throw error;
      }
    }

    // Check HALF_OPEN request limit
    if (this.metrics.state === 'HALF_OPEN') {
      if (this.metrics.halfOpenRequests >= this.config.halfOpenMaxRequests) {
        const nextAttempt = new Date(
          this.metrics.lastStateChangeTime + this.config.timeout
        );
        const error = new CircuitBreakerOpenError(nextAttempt);
        if (this.hooks.onRejected) {
          this.hooks.onRejected(error);
        }
        throw error;
      }

      const requestNumber = ++this.metrics.halfOpenRequests;
      if (this.hooks.onHalfOpenRequest) {
        this.hooks.onHalfOpenRequest(requestNumber);
      }
    }

    try {
      const result = await fn();
      this.recordSuccess();
      return result;
    } catch (error) {
      this.recordFailure(error as Error);
      throw error;
    }
  }

  /**
   * Record successful request
   */
  private recordSuccess(): void {
    this.metrics.lastSuccessTime = Date.now();
    this.metrics.failureCount = 0;

    if (this.metrics.state === 'HALF_OPEN') {
      this.metrics.successCount++;

      // Close circuit if success threshold reached
      if (this.metrics.successCount >= this.config.successThreshold) {
        this.transitionTo('CLOSED');
        if (this.hooks.onClosed) {
          this.hooks.onClosed(this.metrics.successCount);
        }
        this.metrics.successCount = 0;
        this.metrics.halfOpenRequests = 0;
      }
    }
  }

  /**
   * Record failed request
   */
  private recordFailure(error: Error): void {
    this.lastError = error;
    this.metrics.lastFailureTime = Date.now();
    this.metrics.failureCount++;
    this.metrics.successCount = 0;
    this.metrics.halfOpenRequests = 0;

    // Open circuit if failure threshold exceeded
    if (this.metrics.failureCount >= this.config.failureThreshold) {
      this.transitionTo('OPEN');
      if (this.hooks.onOpen) {
        this.hooks.onOpen(this.metrics.failureCount, error);
      }
    }
  }

  /**
   * Transition between states with logging
   */
  private transitionTo(newState: CircuitBreakerState): void {
    const oldState = this.metrics.state;
    if (oldState === newState) {
      return;
    }

    this.metrics.state = newState;
    this.metrics.lastStateChangeTime = Date.now();

    if (typeof console !== 'undefined') {
      console.debug(`Circuit breaker: ${oldState} → ${newState}`);
    }

    if (newState === 'HALF_OPEN' && this.hooks.onHalfOpen) {
      this.hooks.onHalfOpen();
    }
  }

  /**
   * Validate configuration
   */
  private validateConfig(): void {
    if (this.config.failureThreshold < 1) {
      throw new Error('failureThreshold must be at least 1');
    }

    if (this.config.successThreshold < 1) {
      throw new Error('successThreshold must be at least 1');
    }

    if (this.config.timeout < 0) {
      throw new Error('timeout must be non-negative');
    }

    if (this.config.halfOpenMaxRequests < 1) {
      throw new Error('halfOpenMaxRequests must be at least 1');
    }
  }
}
